import React, { useState } from 'react';
import { Search, X, Check, Tag, MapPin, SlidersHorizontal } from 'lucide-react';

const EventsFilters = ({
  searchTerm,
  onSearchChange,
  selectedTypes,
  onTypeToggle,
  selectedModes,
  onModeToggle,
  eventTypes,
  eventModes,
  clearTypeFilters,
  clearModeFilters,
  onClearFilters
}) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  
  const hasActiveTypes = selectedTypes.length > 0;
  const hasActiveModes = selectedModes.length > 0;
  const hasSearch = searchTerm.length > 0;
  const hasActiveFilters = hasActiveTypes || hasActiveModes || hasSearch;
  const activeFiltersCount = [hasActiveTypes, hasActiveModes, hasSearch].filter(Boolean).length;

  return (
    <div className="w-full mb-8">
      {/* Search Bar */}
      <div className="relative flex flex-col sm:flex-row gap-3 bg-white rounded-2xl shadow-lg border border-gray-100 p-2">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-12 pr-4 py-4 text-base bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1.5 bg-gray-200 hover:bg-gray-300 rounded-full"
            >
              <X size={14} className="text-gray-600" />
            </button>
          )}
        </div>

        <button
          onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          className={`px-6 py-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
            isFiltersOpen || hasActiveFilters
              ? 'bg-blue-600 text-white shadow-lg'
              : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          <SlidersHorizontal size={20} />
          <span className="hidden sm:inline">Filters</span>
          {hasActiveFilters && (
            <span className="ml-1 w-5 h-5 text-xs font-bold bg-white text-blue-600 rounded-full flex items-center justify-center">
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>

      {/* Active Filters */}
      {hasActiveFilters && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-400 uppercase tracking-wider mr-1">Active:</span>
          
          {searchTerm && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full">
              <Search size={14} className="text-blue-600" />
              <span className="text-sm text-blue-700">"{searchTerm}"</span>
              <button onClick={() => onSearchChange('')} className="p-0.5 hover:bg-blue-200 rounded-full">
                <X size={14} className="text-blue-600" />
              </button>
            </div>
          )}

          {hasActiveTypes && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full">
              <Tag size={14} className="text-purple-600" />
              <span className="text-sm text-purple-700">
                {eventTypes.find(t => t.id.toString() === selectedTypes[0])?.name || 'Type'}
              </span>
              <button onClick={clearTypeFilters} className="p-0.5 hover:bg-purple-200 rounded-full">
                <X size={14} className="text-purple-600" />
              </button>
            </div>
          )}

          {hasActiveModes && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full">
              <MapPin size={14} className="text-amber-600" />
              <span className="text-sm text-amber-700">
                {eventModes.find(m => m.id.toString() === selectedModes[0])?.name || 'Mode'}
              </span>
              <button onClick={clearModeFilters} className="p-0.5 hover:bg-amber-200 rounded-full">
                <X size={14} className="text-amber-600" />
              </button>
            </div>
          )}

          <button
            onClick={onClearFilters}
            className="ml-2 px-4 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-all"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Filters Panel */}
      {isFiltersOpen && (
        <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Event Types */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-700">
                  <div className="p-1.5 bg-purple-50 rounded-lg">
                    <Tag size={16} className="text-purple-600" />
                  </div>
                  <h4 className="font-medium">Event Type</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {eventTypes.map(type => {
                    const isSelected = selectedTypes.includes(type.id.toString());
                    return (
                      <button
                        key={type.id}
                        onClick={() => onTypeToggle(type.id)}
                        className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                          isSelected
                            ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className={isSelected ? 'text-white' : 'text-purple-600'}>
                            {type.icon}
                          </span>
                          {type.name}
                          {isSelected && <Check size={14} className="ml-1" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Event Modes */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-700">
                  <div className="p-1.5 bg-amber-50 rounded-lg">
                    <MapPin size={16} className="text-amber-600" />
                  </div>
                  <h4 className="font-medium">Event Mode</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {eventModes.map(mode => {
                    const isSelected = selectedModes.includes(mode.id.toString());
                    return (
                      <button
                        key={mode.id}
                        onClick={() => onModeToggle(mode.id)}
                        className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                          isSelected
                            ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/30'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className={isSelected ? 'text-white' : 'text-amber-600'}>
                            {mode.icon}
                          </span>
                          {mode.name}
                          {isSelected && <Check size={14} className="ml-1" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsFilters;